// Console V4 Settings back end (src/aithora/v4-settings-routes.ts, src/aithora/stepup.ts,
// src/aithora/v3/channels.ts, the Linear routes in router.ts, account-deletion-routes.ts).
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type { AccountKind, DeletionRequest } from './account-routes';

const A = AITHORA_PREFIX;

// ── My connections (Spec V4 §16 F) ──────────────────────────────────────────────

export interface ConsentOnFile {
  onFile: boolean;
  /** The exact words agreed to. */
  wording: string | null;
  /** 'console' | 'app' (the person's own box), 'verbal' | 'paper' (recorded by an
   *  administrator), 'sms_keyword' (texted START). */
  source: string | null;
  recordedAt: string | null;
  recordedByName: string | null;
}

export type PhoneState = 'verified' | 'unverified' | 'optout' | 'none';
export type PinStateName = 'set' | 'unset' | 'locked';

export interface MyConnections {
  real: true;
  role: string;
  /** Super Admin, Operator or Developer: Linear and the Google tools apply. */
  team: boolean;
  name: string | null;
  phone: {
    /** "••• ••• 4417", or null when no number. */
    masked: string | null;
    state: PhoneState;
    /** Care Staff: their Facility Admin sets the number (read only here). */
    staffSet: boolean;
  };
  /** The number waiting for its 6-digit code, masked, or null. */
  pendingPhone: string | null;
  /** Where the code is confirmed (POST { code }), e.g. /api/aithora/users/<id>/phone/verify. */
  verifyPath: string;
  /** Whether texting is switched on; reason when not ("Texting is not switched on yet"). */
  sms: { live: boolean; reason: string | null };
  consent: ConsentOnFile;
  /** The exact opt-in sentence to show and record. Every alert text and call re-checks
   *  consent right before it goes (AF-1566): none on file means push and in-app only. */
  consentWording: string;
  /** Null for a family member (no PIN). */
  pin: { state: PinStateName } | null;
  /** Null unless team. last4 is the last 4 of their own key, never the key. */
  linear: { connected: boolean; last4: string | null } | null;
}

/** GET /me/connections */
export const meConnections = route<MyConnections>()('GET', `${A}/me/connections`, { honest: true });
/** POST /me/connections/consent { granted?, source? }: tick (or untick) my own box on my
 *  verified number; the words recorded are the server's. 409 when opted out (STOP). */
export const meConnectionsConsent = route<MyConnections, void, { granted?: boolean; source?: 'console' | 'app' }>()('POST', `${A}/me/connections/consent`, { honest: true });
/** POST /users/:id/contact-consent { source: 'verbal' | 'paper', wording, granted?, note? }: Aithora,
 *  or a Facility Admin for their own building's staff. granted:false records a withdrawal. */
export const userContactConsent = route<{ real: true; consent: ConsentOnFile }, void, { source: 'verbal' | 'paper'; wording: string; granted?: boolean; note?: string }>()(
  'POST', `${A}/users/:id/contact-consent`, { honest: true });

/** A building's contact number (the Pulse downtime fallback texts it) and its consent. */
export interface BuildingContactConsent {
  real: true;
  /** "••• ••• 0100", or null when the building has no contact number on file. */
  phone: string | null;
  consent: ConsentOnFile;
  /** The number replied STOP: nothing can be recorded over it. */
  optedOut: boolean;
  /** The words to record (the V4 opt-in sentence). */
  wording: string;
}
/** GET /facilities/:id/contact-consent (:id or 'me'): Aithora, or the building's own Facility Admin. */
export const facilityContactConsent = route<BuildingContactConsent>()('GET', `${A}/facilities/:id/contact-consent`, { honest: true });
/** POST /facilities/:id/contact-consent { source: 'verbal' | 'paper', wording, granted?, note? }:
 *  consent given for the building's contact number on file (never a number typed in). */
export const facilityContactConsentRecord = route<BuildingContactConsent, void, { source: 'verbal' | 'paper'; wording: string; granted?: boolean; note?: string }>()(
  'POST', `${A}/facilities/:id/contact-consent`, { honest: true });

export interface TestTextResult {
  real: true;
  sent: boolean;
  /** Texting is not switched on yet: nothing was sent. */
  notLive?: boolean;
  reason?: string;
  at?: string;
}
/** POST /channels/test: one test text to my own verified number (400 not verified, 409 no consent or opted out). */
export const channelsTest = route<TestTextResult, void, Record<string, never>>()('POST', `${A}/channels/test`, { honest: true });

/** POST /channels/phone { phone }: start verifying my own number (not Care Staff). An empty body resends the code to my number on file (400 if none). */
export const userPhoneStart = route<{ real: true; live: boolean; reason: string | null; pendingPhone: string | null; verifyAt: string }, void, { phone?: string }>()(
  'POST', `${A}/channels/phone`, { honest: true });
/** POST /users/:id/phone/verify { code, consent?, source? } (same route as userAlertPhoneVerify):
 *  confirm my own number; consent: true is my own ticked box, recorded once the code is right. */
export const userPhoneVerify = route<{ real: true; phone: string | null; verified: true; consent?: ConsentOnFile }, void, { code: string; consent?: boolean; source?: 'console' | 'app' }>()(
  'POST', `${A}/users/:id/phone/verify`, { honest: true });

/** POST /channels/pin { pin, stepUpToken | password | code }: set or reset my text PIN. */
export const channelsPinSet = route<{ real: true; set: boolean; locked: boolean; setAt: string | null }, void, { pin: string; stepUpToken?: string; password?: string; code?: string }>()(
  'POST', `${A}/channels/pin`, { honest: true });

/** GET /me/connections/linear */
export const meLinearStatus = route<{ real: true; connected: boolean; account: string | null; source: 'db' | 'env' | null }>()('GET', `${A}/me/connections/linear`, { honest: true });
/** POST /me/connections/linear { key }: checked with Linear, stored encrypted. */
export const meLinearConnect = route<{ real: true; connected: true; account: string | null }, void, { key: string }>()('POST', `${A}/me/connections/linear`, { honest: true });
/** DELETE /me/connections/linear: the key is deleted. */
export const meLinearDisconnect = route<{ real: true; connected: false; removed: boolean }>()('DELETE', `${A}/me/connections/linear`, { honest: true });

// ── Passkey step-up ("Confirm with Face ID or passkey") ─────────────────────────

/** POST /2fa/stepup/options: WebAuthn request options (400 { noPasskey: true } with none). */
export const stepUpOptions = route<Record<string, unknown>, void, Record<string, never>>()('POST', `${A}/2fa/stepup/options`);
/** POST /2fa/stepup/verify { response }: a single-use token for the next write (5 min). */
export const stepUpVerify = route<{ real: true; stepUpToken: string; expiresAt: string }, void, { response: unknown }>()('POST', `${A}/2fa/stepup/verify`, { honest: true });

// ── Assistant settings (Spec V4 §16 G), Aithora only ────────────────────────────

export type AssistantProvider = 'anthropic' | 'local' | 'mistral';
export type AssistantShadow = 'off' | 'local' | 'mistral';

export interface AssistantAdminSettings {
  real: true;
  /** The owner (by email) edits; an Operator reads. */
  canEdit: boolean;
  model: AssistantProvider;
  shadow: AssistantShadow;
  chatConfirm: boolean;
  /** "Team answers by text" (its older name; V5 sends teamAnswersText too). */
  teamFreeform: boolean;
  /** V5: two switches, by text and on WhatsApp (an older server sends teamFreeform only). */
  teamAnswersText?: boolean;
  teamAnswersWhatsApp?: boolean;
  candidates: Array<{
    key: 'local' | 'mistral';
    /** Always true until the exam and shadow rule pass (model_graduations not trusted until AF-1579). */
    locked: boolean;
    exam: { jobsPassed: number; jobsTotal: number; criticalFailures: number | null; shadowDays: number };
  }>;
  limits: Array<{ key: 'yes_window' | 'pin_tries' | 'per_person_caps'; value: string; note: string }>;
  /** Changes in the last 24 hours, newest first. */
  log: Array<{ key: string; value: string; at: string; actorName: string | null; reason: string | null; gated: boolean }>;
}

/** GET /assistant/admin-settings */
export const assistantAdminSettings = route<AssistantAdminSettings>()('GET', `${A}/assistant/admin-settings`, { honest: true });
/** POST /assistant/admin-settings: owner only; reason + step-up for all but Back to Claude. */
export const assistantAdminSettingsSave = route<
  AssistantAdminSettings,
  void,
  { key: 'model' | 'shadow' | 'chatConfirm' | 'teamFreeform' | 'teamAnswersText' | 'teamAnswersWhatsApp'; value: string | boolean; reason?: string; stepUpToken?: string; password?: string; code?: string }
>()('POST', `${A}/assistant/admin-settings`, { honest: true });

// ── Account deletion, the facility side (Spec V4 step 3) ────────────────────────

export interface FacilityDeletionRequest extends DeletionRequest {
  matches: Array<{ userId: string; role: string; kind: AccountKind; organizationId: string | null; organizationName: string | null; name: string | null }>;
}

/** GET /account/deletion-requests: residents' requests waiting on the facility. */
export const facilityDeletionRequests = route<{ real: true; requests: FacilityDeletionRequest[] }>()('GET', `${A}/account/deletion-requests`, { honest: true });
/** POST /account/deletion-requests/:id/execute { verification, confirm: 'DELETE' } */
export const facilityDeletionRequestExecute = route<
  { real: true; deleted: true; kind: 'resident'; momentFilesRemoved: number; ledgerEntryId: string | null },
  void,
  { verification: string; confirm: 'DELETE' }
>()('POST', `${A}/account/deletion-requests/:id/execute`, { honest: true });
/** POST /account/deletion-requests/:id/decline { reason } (the resident is told). */
export const facilityDeletionRequestDecline = route<{ real: true; request: DeletionRequest }, void, { reason: string }>()(
  'POST', `${A}/account/deletion-requests/:id/decline`, { honest: true });
